import {
  ButtonDS,
  DeleteOutlined,
  DialogModalDS,
  Spacer,
  TableDS,
  TextCapitalized,
} from "@qivia/ui";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useNavigate } from "react-router-dom";
import { selectCompany } from "../../homeSlice";
import { ManagerDataDisplayRow } from "../usersAPI";
import { selectAllCompanies } from "../../companies/companiesSlice";
import "../../../../i18n/i18n";
import { EmptyTable } from "@qivia/ui/src/assets/assets";
import type { TableHeaderProps } from "@qivia/ui/src/designSystem/components/table/TableHeader";
import { removeCompaniesFromCompaniesAccessAsync } from "../usersSlice";

type CompanyItemDisplayed = { label: string };
type CompanyItem = CompanyItemDisplayed & { value: string };

export const RemoveCompaniesFromCompaniesAccessModal = (props: {
  isVisible: boolean;
  onCloseModal: () => void;
  manager: ManagerDataDisplayRow;
}) => {
  const { t } = useTranslation();
  const closeModal = props.onCloseModal;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const allCompanies = useAppSelector(selectAllCompanies);
  const [rowsChecked, setRowsChecked] = useState<CompanyItem[]>([]);
  const [displayError, setDisplayError] = useState<boolean>(false);

  const onRowsCheckedChange = useCallback((rows: CompanyItem[]) => {
    setRowsChecked(rows);
  }, []);

  const companiesOptions: CompanyItem[] = useMemo(
    () => {
      if (!props.manager.companiesAccess || !allCompanies) return [];

      const options = props.manager.companiesAccess.map((uuid) => {
        const company = allCompanies.find((c) => c.uuid === uuid);
        if (!company) return null;

        return {
          label: company.name,
          value: uuid,
        };
      });

      return options.filter((option) => option !== null);
    },
    [allCompanies, props.manager.companiesAccess],
  );

  const errorMessage = useMemo(() => {
    if (!displayError) return;

    if (rowsChecked.length === 0) {
      return t("users.management.remove.user.modal.error.selectionEmpty");
    }

    if (
      props.manager.companiesAccess &&
      rowsChecked.length === props.manager.companiesAccess.length
    ) {
      return t("users.management.remove.user.modal.error.allSelection");
    }
  }, [displayError, rowsChecked.length, props.manager.companiesAccess, t]);

  const isValidSelection = useMemo(() => {
    return (
      rowsChecked.length > 0 &&
      rowsChecked.length < (props.manager.companiesAccess?.length ?? 0)
    );
  }, [rowsChecked.length, props.manager.companiesAccess?.length]);
  
  const handleSubmitRemoveCompanyFromCompaniesAccess = useCallback(() => {
    if (!company || !props.manager.uuid) return;
  
    if (!isValidSelection) {
      setDisplayError(true);
      return;
    }
  
    const selectedUuids = rowsChecked.map((item) => item.value);
    void dispatch(removeCompaniesFromCompaniesAccessAsync({ managerUuid: props.manager.uuid, companyUuids: selectedUuids }));
    setDisplayError(false);
    props.onCloseModal();
    navigate(`/home/users/management`);
  }, [company, props, isValidSelection, rowsChecked, dispatch, navigate]);

  const handleCloseModal = useCallback(() => {
    setDisplayError(false);
    setRowsChecked([]);
    closeModal();
  }, [closeModal]);
  

  const headers = {
    label: {
      text: t("users.management.remove.user.modal.table.label", {
        selected: rowsChecked?.length,
      }),
    },
  };

  const render = (row: CompanyItem) => (key: keyof CompanyItemDisplayed) => row[key];

  return (
    <DialogModalDS
      width={40}
      visible={props.isVisible}
      onClose={handleCloseModal}
      title={t("users.management.remove.user.modal.title", {
        name: props.manager.name,
      })}
      subTitle={t("users.management.remove.user.modal.subtitle")}
      iconTitle={
        <StyledIcon>
          <DeleteOutlined />
        </StyledIcon>
      }
      bottomButtons={{
        leftButton: (
          <ButtonDS
            text={t("users.management.remove.user.modal.button.cancel")}
            format={"fill"}
            buttonType={"secondary"}
            onClick={props.onCloseModal}
          />
        ),
        rightButton: (
          <ButtonDS
            text={t("users.management.remove.user.modal.button.confirm")}
            format={"fill"}
            buttonType={"primary"}
            onClick={handleSubmitRemoveCompanyFromCompaniesAccess}
            disabled={!isValidSelection}
          />
        ),
      }}
    >
      <StyledContainer>
      <TableDS<keyof CompanyItemDisplayed, CompanyItem>
        data={companiesOptions}
        headers={headers as Record<keyof CompanyItem, TableHeaderProps>}
        render={render}
        rowsChecked={rowsChecked}
        setRowsChecked={onRowsCheckedChange}
        height={15}
        width={36}
        header-checkbox-data-cy="header-checkbox-data-cy"
        emptyContent={
          <StyledFlex>
            <Spacer x={1.5} />
            <StyledEmptyTable>
              <Spacer y={2} />
              <EmptyTable />
              <Spacer y={1.5} />
              <TextCapitalized>
                {t("users.management.remove.user.modal.listEmpty")}
              </TextCapitalized>
              <Spacer y={2} />
            </StyledEmptyTable>
            <Spacer x={1.5} />
          </StyledFlex>
        }
      />
      {displayError && errorMessage && (
        <>
          <Spacer y={1} />
          <StyledError>
            <TextCapitalized>{errorMessage}</TextCapitalized>
          </StyledError>
        </>
      )}
      </StyledContainer>
    </DialogModalDS>
  );
};

const StyledFlex = styled.div`
  display: flex;
`;

const StyledEmptyTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  text-align: center;

  & svg {
    width: 5rem;
    height: 5rem;
  }
`;

const StyledError = styled.div`
  display: flex;
  flex-direction: column;
  color: ${colors["colors/system/error/error_normal"]};
`;

const StyledIcon = styled.div`
  background-color: ${colors["colors/surfaces/background/background_level2"]};
  color: ${colors["colors/text/black"]};
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
